<template>
  <div class="project-confirmation-container">
    <!-- Header -->
    <div class="confirmation-header">
      <div class="success-indicator">
        <span class="success-icon">✅</span>
        <div class="success-text">
          <h1>Project Created Successfully!</h1>
          <p>Your project has been created and is ready for document uploads</p>
        </div>
      </div>
    </div>

    <!-- Project Summary Report -->
    <div class="project-report-card">
      <!-- Project Title -->
      <div class="project-title-section">
        <h2>{{ projectData.name }}</h2>
        <div class="project-id">Project #{{ projectData.id }}</div>
      </div>

      <!-- Visual Section: Image + Map -->
      <div class="visual-section">
        <!-- Project Image -->
        <div class="project-image-container">
          <h3>Building Image</h3>
          <div class="image-wrapper">
            <img 
              v-if="projectData.image_url" 
              :src="projectData.image_url" 
              :alt="projectData.name + ' - Building View'"
              class="project-image"
            />
            <div v-else class="image-placeholder">
              <span class="placeholder-icon">🏢</span>
              <p>No image uploaded</p>
            </div>
          </div>
        </div>

        <!-- Thumbnail Map -->
        <div class="map-container">
          <h3>Project Location</h3>
          <div class="map-wrapper">
            <div v-if="!isProjectLocationValid" class="map-error">
              Project has invalid location values.
            </div>
            <div v-else id="confirmationPageMap" class="confirmation-map"></div>
          </div>
        </div>
      </div>

      <!-- Project Details Grid -->
      <div class="project-details-grid">
        <!-- Location Information -->
        <div class="detail-section">
          <h3>Location Information</h3>
          <div class="detail-item">
            <label>Address:</label>
            <span>{{ fullAddress }}</span>
          </div>
          <div class="detail-item">
            <label>City:</label>
            <span>{{ getDisplayValue(projectData.city) }}</span>
          </div>
          <div class="detail-item">
            <label>State:</label>
            <span>{{ getDisplayValue(projectData.state) }}</span>
          </div>
          <div class="detail-item">
            <label>Zip Code:</label>
            <span>{{ getDisplayValue(projectData.zipcode) }}</span>
          </div>
          <div class="detail-item" v-if="projectData.latitude && projectData.longitude">
            <label>Coordinates:</label>
            <span>{{ projectData.latitude }}, {{ projectData.longitude }}</span>
          </div>
        </div>

        <!-- Building Information -->
        <div class="detail-section">
          <h3>Building Information</h3>
          <div class="detail-item">
            <label>Construction Type:</label>
            <span>{{ getDisplayValue(getConstructionTypesDisplay()) }}</span>
          </div>
          <div class="detail-item">
            <label>Former Use Type:</label>
            <span>{{ getDisplayValue(getFormerUseTypesDisplay()) }}</span>
          </div>
          <div class="detail-item">
            <label>Total GSF:</label>
            <span>{{ formatNumber(projectData.total_gsf) }}</span>
          </div>
          <div class="detail-item">
            <label>Unit Count:</label>
            <span>{{ formatNumber(projectData.num_units) }}</span>
          </div>
          <div class="detail-item">
            <label>Number of Floors:</label>
            <span>{{ formatNumber(projectData.num_floors) }}</span>
          </div>
          <div class="detail-item">
            <label>Parking Count:</label>
            <span>{{ formatNumber(projectData.parking_count) }}</span>
          </div>
        </div>

        <!-- Team Information -->
        <div class="detail-section">
          <h3>Project Team</h3>
          <div class="detail-item" v-if="projectData.primaryGeneralContractor?.name">
            <label>General Contractor:</label>
            <span>{{ projectData.primaryGeneralContractor.name }}</span>
          </div>
          <div class="detail-item" v-if="projectData.primaryGeneralContractor?.is_affiliated_with_developer">
            <label>Affiliated with Developer:</label>
            <span>Yes</span>
          </div>
          
          <!-- Additional Team Members -->
          <div class="detail-item" v-if="projectData.team_contractors && projectData.team_contractors.length > 0">
            <label>Contractors:</label>
            <span>{{ projectData.team_contractors.join(', ') }}</span>
          </div>
          <div class="detail-item" v-if="projectData.team_consultants && projectData.team_consultants.length > 0">
            <label>Consultants:</label>
            <span>{{ projectData.team_consultants.join(', ') }}</span>
          </div>
          <div class="detail-item" v-if="projectData.team_investors && projectData.team_investors.length > 0">
            <label>Investors:</label>
            <span>{{ projectData.team_investors.join(', ') }}</span>
          </div>
          
          <!-- Show message if no team data -->
          <div class="detail-item" v-if="!projectData.primaryGeneralContractor?.name && 
                                         (!projectData.team_contractors || projectData.team_contractors.length === 0) &&
                                         (!projectData.team_consultants || projectData.team_consultants.length === 0) &&
                                         (!projectData.team_investors || projectData.team_investors.length === 0)">
            <label>Team:</label>
            <span class="empty-value">No team members added yet</span>
          </div>
        </div>
        
        <!-- Amenities Information (if any) -->
        <div class="detail-section" v-if="projectData.amenities && projectData.amenities.length > 0">
          <h3>Amenities</h3>
          <div class="detail-item">
            <label>Amenities:</label>
            <span>{{ projectData.amenities.join(', ') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Document Upload Section -->
    <div class="upload-invitation-section">
      <div class="upload-header">
        <h2>Ready to Upload Your First Document?</h2>
        <p>Get started by uploading a draw tracker document to begin managing your project budget and progress</p>
      </div>

      <div class="upload-actions-simple">
        <button class="upload-button-main" @click="showUploadModal">
          Upload Your First Document
        </button>
        <p>Or you can always upload documents later from the project dashboard</p>
        <button class="secondary-button" @click="goToProject">
          Go to Project Dashboard
        </button>
      </div>
    </div>

    <!-- Upload Draw Modal (reused from ProjectSummary) -->
    <div v-if="showUploadDrawModal" class="modal-backdrop" @click="closeUploadModal">
      <div class="upload-draw-modal" @click.stop :key="uploadModalKey">
        <div class="modal-header">
          <h3>Upload First Document - Draw 1</h3>
          <button class="close-button" @click="closeUploadModal">×</button>
        </div>
        
        <div class="modal-body">
          <!-- Step 1: File Upload -->
          <div v-if="!uploadedDocumentId && !showOCRProgress && !showExtractionResults">
            <!-- Template Selection -->
            <div class="form-section">
              <label class="form-label">Select Template Type</label>
              <select v-model="selectedTemplate" class="form-select">
                <option value="">Choose template type...</option>
                <option 
                  v-for="template in availableTemplates" 
                  :key="template.id" 
                  :value="template.value"
                >
                  {{ template.name }}
                </option>
              </select>
            </div>

            <!-- Upload Section -->
            <div class="upload-section" v-if="selectedTemplate">
              <div class="upload-instructions">
                <p>Upload a draw tracker document (Excel/PDF) to create a new draw template</p>
                <small>Supported formats: .xlsx, .xls, .pdf</small>
              </div>
              
              <div class="file-upload-area" 
                   @dragover.prevent 
                   @drop.prevent="handleFileDrop"
                   :class="{ 'drag-over': isDragOver }"
                   @dragenter.prevent="isDragOver = true"
                   @dragleave.prevent="isDragOver = false">
                <div v-if="!selectedFile">
                  <div class="upload-placeholder">
                    <span class="upload-icon-large">📄</span>
                    <p>Drag and drop files here or <button class="browse-button" @click="triggerFileInput">browse</button></p>
                  </div>
                </div>
                <div v-else class="file-selected">
                  <div class="file-info">
                    <span class="file-icon">📄</span>
                    <span class="file-name">{{ selectedFile.name }}</span>
                    <button class="remove-file" @click="removeFile">×</button>
                  </div>
                </div>
                <input 
                  type="file" 
                  ref="fileInput" 
                  @change="handleFileSelect" 
                  accept=".xlsx,.xls,.pdf" 
                  style="display: none"
                />
              </div>

              <!-- Upload Button -->
              <div class="upload-actions" v-if="selectedFile && selectedTemplate">
                <button 
                  class="upload-button" 
                  @click="uploadDrawDocument"
                  :disabled="isUploading"
                >
                  <span v-if="isUploading">Uploading...</span>
                  <span v-else>Upload & Configure Template</span>
                </button>
              </div>

              <!-- Upload Progress -->
              <div v-if="isUploading" class="progress-section">
                <div class="progress-bar-container">
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
                  </div>
                  <span class="progress-text">{{ uploadProgress }}%</span>
                </div>
                <p class="progress-status">{{ uploadStatus }}</p>
              </div>
            </div>
          </div>

          <!-- OCR Progress Display -->
          <div v-if="showOCRProgress">
            <div class="ocr-progress-container">
              <h4>Processing Document</h4>
              <div class="progress-steps">
                <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                  <span class="step-number">1</span>
                  <span class="step-text">Uploading</span>
                </div>
                <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
                  <span class="step-number">2</span>
                  <span class="step-text">OCR Processing</span>
                </div>
                <div class="step" :class="{ active: currentStep >= 3, completed: currentStep > 3 }">
                  <span class="step-number">3</span>
                  <span class="step-text">Data Extraction</span>
                </div>
              </div>
              <p class="progress-message">{{ progressMessage }}</p>
            </div>
          </div>

          <!-- Results Display -->
          <div v-if="showExtractionResults">
            <div class="results-container">
              <h4>Document Processed Successfully!</h4>
              <p>Your document has been processed and is ready for template configuration.</p>
              <div class="results-actions">
                <button class="primary-button" @click="proceedToTemplateSetup">
                  Configure Template
                </button>
                <button class="secondary-button" @click="closeUploadModal">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { loadGoogleMapsAPI } from '@/main.js'

export default {
  name: 'ProjectConfirmationView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    
    // Project data passed from creation
    const projectData = ref({})
    const isProjectLocationValid = ref(false)
    
    // Upload related state (modal-based)
    const showUploadDrawModal = ref(false)
    const uploadModalKey = ref(0)
    const selectedTemplate = ref('')
    const selectedFile = ref(null)
    const isDragOver = ref(false)
    const isUploading = ref(false)
    const uploadProgress = ref(0)
    const uploadStatus = ref('')
    const fileInput = ref(null)
    const uploadedDocumentId = ref(null)
    const showOCRProgress = ref(false)
    const showExtractionResults = ref(false)
    const currentStep = ref(1)
    const progressMessage = ref('')
    
    // Map related
    let map = null
    
    // Available templates (EXACT same as ProjectSummary)
    const availableTemplates = ref([
      { id: 'pdf_multi_doc', name: 'PDF Document (G702/G703/Lien Waiver)', value: 'pdf_multi_doc', template_type: 'pdf_multi_doc' },
      { id: 'excel_draw_tracker', name: 'Excel/XLS Document', value: 'excel_draw_tracker', template_type: 'excel_draw_tracker' }
    ])

    // Computed properties
    const fullAddress = computed(() => {
      if (!projectData.value.address_line_1) return 'No address provided'
      let address = projectData.value.address_line_1
      if (projectData.value.address_line_2) {
        address += `, ${projectData.value.address_line_2}`
      }
      return address
    })

    // Utility functions
    const getDisplayValue = (value) => {
      return value && value !== '' ? value : 'Not specified'
    }

    const formatNumber = (value) => {
      if (!value || value === 0) return 'Not specified'
      return new Intl.NumberFormat().format(value)
    }

    const getSelectedTemplateName = () => {
      const template = availableTemplates.value.find(t => t.value === selectedTemplate.value)
      return template ? template.name : 'document'
    }

    const getConstructionTypesDisplay = () => {
      if (!projectData.value.project_construction_types_s) return null
      if (Array.isArray(projectData.value.project_construction_types_s)) {
        return projectData.value.project_construction_types_s.join('; ')
      }
      return projectData.value.project_construction_types_s
    }

    const getFormerUseTypesDisplay = () => {
      if (!projectData.value.former_use_types_s) return null
      if (Array.isArray(projectData.value.former_use_types_s)) {
        return projectData.value.former_use_types_s.join('; ')
      }
      return projectData.value.former_use_types_s
    }

    // Load project data from route params or localStorage
    const loadProjectData = () => {
      // Try to get from route params first (passed during redirect)
      if (route.params.projectData) {
        projectData.value = JSON.parse(route.params.projectData)
      } else {
        // Fallback: get from localStorage (in case of page refresh)
        const storedData = localStorage.getItem('newProjectData')
        if (storedData) {
          projectData.value = JSON.parse(storedData)
          // Clear from localStorage after use
          localStorage.removeItem('newProjectData')
        } else {
          // Last resort: redirect back to new project if no data
          router.push('/project')
          return
        }
      }

      // Validate location and setup map
      if (projectData.value.latitude && projectData.value.longitude) {
        isProjectLocationValid.value = true
        loadGoogleMapsAPI.then(() => {
          nextTick(() => {
            loadGoogleMapsWithCoordinates(
              parseFloat(projectData.value.latitude),
              parseFloat(projectData.value.longitude)
            )
          })
        })
      }
    }

    // Map functions (adapted from ProjectSummary)
    const loadGoogleMapsWithCoordinates = (lat, lng) => {
      const parser = new DOMParser()
      const pinSvgString = 
        '<svg xmlns="http://www.w3.org/2000/svg" width="5.6444445mm" height="9.847393mm" viewBox="0 0 20 34.892337" version="1.1" >\n' +
        '  <g transform="translate(-814.59595,-274.38623)">\n' +
        '    <g transform="matrix(1.1855854,0,0,1.1855854,-151.17715,-57.3976)">\n' +
        '      <path d="m 817.11249,282.97118 c -1.25816,1.34277 -2.04623,3.29881 -2.01563,5.13867 0.0639,3.84476 1.79693,5.3002 4.56836,10.59179 0.99832,2.32851 2.04027,4.79237 3.03125,8.87305 0.13772,0.60193 0.27203,1.16104 0.33416,1.20948 0.0621,0.0485 0.19644,-0.51262 0.33416,-1.11455 0.99098,-4.08068 2.03293,-6.54258 3.03125,-8.87109 2.77143,-5.29159 4.50444,-6.74704 4.56836,-10.5918 0.0306,-1.83986 -0.75942,-3.79785 -2.01758,-5.14062 -1.43724,-1.53389 -3.60504,-2.66908 -5.91619,-2.71655 -2.31115,-0.0475 -4.4809,1.08773 -5.91814,2.62162 z" style="display:inline;opacity:1;fill:#4A5FD5;fill-opacity:1;stroke:#3b73a8;stroke-width:1;stroke-miterlimit:4;stroke-dasharray:none;stroke-opacity:1"/>\n' +
        '      <circle r="3.0355" cy="288.25278" cx="823.03064" style="display:inline;opacity:1;fill:#fff;fill-opacity:1;stroke-width:0"/>\n' +
        '    </g>\n' +
        '  </g>\n' +
        '</svg>\n'

      const circleCenterLocation = { lat: lat, lng: lng }
      const mapOptions = {
        center: circleCenterLocation,
        zoom: 14,
        mapId: 'confirmationPageMap',
        mapTypeId: 'terrain'
      }

      const mapElement = document.getElementById('confirmationPageMap')
      if (!mapElement) return

      map = new google.maps.Map(mapElement, mapOptions)
      
      // Create the marker
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: circleCenterLocation,
        map: map,
        content: parser.parseFromString(pinSvgString, "image/svg+xml").documentElement
      })

      // Info window
      const infoWindow = new google.maps.InfoWindow({
        content: `<div style="background: #3b73a8; color: #fff; font-size: 12px; display: flex; flex-direction: column; align-items: center; height: 100%; padding: 5px; width: 180px">
                  <div><strong>${projectData.value.address_line_1}</strong></div>
                  <div style="display: flex; align-items: center;">
                    <label style="width: 100%;">Construction Type:</label>
                    <div style="white-space:nowrap;">${projectData.value.project_construction_types_s ? projectData.value.project_construction_types_s[0] : '-'}</div>
                  </div>
                  <div style="display: flex; align-items: center;">
                    <label style="width: 100%;">Total GSF:</label>
                    <div style="white-space:nowrap;">${projectData.value.total_gsf ? formatNumber(projectData.value.total_gsf) : '-'}</div>
                  </div>
                  <div style="display: flex; align-items: center;">
                    <label style="width: 100%;">Unit Count:</label>
                    <div style="white-space:nowrap;">${projectData.value.num_units ? formatNumber(projectData.value.num_units) : '-'}</div>
                  </div>
                </div>`
      })

      marker.addListener('click', () => {
        infoWindow.open({
          anchor: marker,
          map,
          shouldFocus: true,
        })
      })
    }

    // File upload functions (adapted from ProjectSummary)
    const triggerFileInput = () => {
      fileInput.value.click()
    }

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) {
        selectedFile.value = file
      }
    }

    const handleFileDrop = (event) => {
      isDragOver.value = false
      const file = event.dataTransfer.files[0]
      if (file) {
        selectedFile.value = file
      }
    }

    const removeFile = () => {
      selectedFile.value = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    // Modal functions
    const showUploadModal = () => {
      showUploadDrawModal.value = true
      uploadModalKey.value += 1
    }

    const closeUploadModal = () => {
      showUploadDrawModal.value = false
      selectedTemplate.value = ''
      selectedFile.value = null
      isUploading.value = false
      uploadProgress.value = 0
      uploadStatus.value = ''
      uploadedDocumentId.value = null
      showOCRProgress.value = false
      showExtractionResults.value = false
    }

    // Upload document function (EXACT same as ProjectSummary)
    const uploadDrawDocument = async () => {
      console.log('Upload draw document called');
      console.log('Form values:', {
        selectedFile: selectedFile.value?.name,
        projectId: projectData.value.id,
        selectedTemplate: selectedTemplate.value,
        drawNumber: '1' // Always Draw 1 for new projects
      });
      
      if (!selectedFile.value || !projectData.value.id || !selectedTemplate.value) {
        console.log('Missing required fields:', {
          file: !!selectedFile.value,
          id: !!projectData.value.id,
          template: !!selectedTemplate.value
        });
        return;
      }
      
      console.log('Selected template:', selectedTemplate.value);
      console.log('Available templates:', availableTemplates.value);
      
      // Check if this is an Excel template (base template OR existing project Excel template)
      const selectedTemplateData = availableTemplates.value.find(t => t.id === selectedTemplate.value || t.value === selectedTemplate.value);
      const isExcelTemplate = selectedTemplate.value === 'excel_draw_tracker' || 
                             selectedTemplateData?.template_type === 'excel_draw_tracker' ||
                             selectedTemplateData?.name?.toLowerCase().includes('excel');
      
      // Check if this is a PDF multi-document template (replaces individual G702/G703 templates)
      const isPdfMultiDocTemplate = selectedTemplate.value === 'pdf_multi_doc' || 
                                    selectedTemplateData?.template_type === 'pdf_multi_doc' ||
                                    selectedTemplateData?.name?.toLowerCase().includes('pdf multi-doc');
      
      // Excel template handling
      if (isExcelTemplate) {
        console.log('Excel template detected - using draw_templates endpoint');
        
        try {
          isUploading.value = true;
          uploadProgress.value = 0;
          uploadStatus.value = 'Creating Excel template...';
          
          // Create form data for draw_templates endpoint
          const formData = new FormData();
          formData.append('draw_template[name]', `${projectData.value.name} - Draw 1 Excel Template`);
          formData.append('draw_template[description]', `Excel tracker template for ${projectData.value.name} Draw 1`);
          formData.append('draw_template[project_id]', projectData.value.id);
          formData.append('draw_template[draw_number]', '1');
          formData.append('draw_template[template_type]', 'excel_draw_tracker');
          
          // Attach the document
          formData.append('document[file]', selectedFile.value);
          formData.append('document[project_id]', projectData.value.id);
          formData.append('document[draw_number]', '1');
          
          uploadProgress.value = 30;
          uploadStatus.value = 'Uploading Excel file...';
          
          // Upload to draw templates endpoint
          const response = await fetch('/api/v1/draw_templates', {
            method: 'POST',
            body: formData
          });
          
          uploadProgress.value = 80;
          
          if (response.ok) {
            const result = await response.json();
            uploadProgress.value = 100;
            uploadStatus.value = 'Excel template created successfully!';
            
            console.log('Excel template created:', result);
            
            // Close modal and redirect to template setup
            closeUploadModal();
            
            if (result.redirect_url) {
              router.push(result.redirect_url);
            } else {
              router.push(`/draw-template-setup/${result.template.id}`);
            }
          } else {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Excel template creation failed');
          }
        } catch (error) {
          console.error('Error creating Excel template:', error);
          uploadProgress.value = 0;
          uploadStatus.value = 'Excel template creation failed';
          isUploading.value = false;
        }
        
        return; // Exit early for Excel files
      }
      
      // PDF Multi-Document template handling
      if (isPdfMultiDocTemplate) {
        console.log('PDF Multi-Document template detected - using draw_templates endpoint');
        
        try {
          isUploading.value = true;
          uploadProgress.value = 0;
          uploadStatus.value = 'Creating PDF multi-document template...';
          
          // Create form data for draw_templates endpoint
          const formData = new FormData();
          formData.append('draw_template[name]', `${projectData.value.name} - Draw 1 PDF Template`);
          formData.append('draw_template[description]', `PDF multi-document template for ${projectData.value.name} Draw 1`);
          formData.append('draw_template[project_id]', projectData.value.id);
          formData.append('draw_template[draw_number]', '1');
          formData.append('draw_template[template_type]', 'pdf_multi_doc');
          
          // Attach the document
          formData.append('document[file]', selectedFile.value);
          formData.append('document[project_id]', projectData.value.id);
          formData.append('document[draw_number]', '1');
          
          uploadProgress.value = 30;
          uploadStatus.value = 'Uploading PDF file...';
          
          // Upload to draw templates endpoint
          const response = await fetch('/api/v1/draw_templates', {
            method: 'POST',
            body: formData
          });
          
          uploadProgress.value = 80;
          
          if (response.ok) {
            const result = await response.json();
            uploadProgress.value = 100;
            uploadStatus.value = 'PDF template created successfully!';
            
            console.log('PDF template created:', result);
            
            // Close modal and redirect to template setup
            closeUploadModal();
            
            // Show success message
            console.log('PDF uploaded! Starting enhanced multi-document processing with page selection.');
            
            // Navigate to enhanced DrawTemplateSetup with PDF page selection (EXACT same as ProjectSummary)
            router.push(`/draw-template-setup/${result.template.id}?template_type=pdf_multi_doc`);
          } else {
            const errorData = await response.json();
            throw new Error(errorData.error || 'PDF template creation failed');
          }
        } catch (error) {
          console.error('Error creating PDF template:', error);
          uploadProgress.value = 0;
          uploadStatus.value = 'PDF template creation failed';
          isUploading.value = false;
        }
        
        return; // Exit early for PDF files
      }
    }

    const proceedToTemplateSetup = () => {
      if (uploadedDocumentId.value) {
        router.push(`/draw-template-setup/${uploadedDocumentId.value}`)
      }
    }

    const goToProject = () => {
      router.push(`/portfolio/project-summary/${projectData.value.id}`)
    }

    onMounted(() => {
      loadProjectData()
    })

    return {
      projectData,
      isProjectLocationValid,
      fullAddress,
      showUploadDrawModal,
      uploadModalKey,
      selectedTemplate,
      selectedFile,
      isDragOver,
      isUploading,
      uploadProgress,
      uploadStatus,
      fileInput,
      uploadedDocumentId,
      showOCRProgress,
      showExtractionResults,
      currentStep,
      progressMessage,
      availableTemplates,
      getDisplayValue,
      formatNumber,
      getSelectedTemplateName,
      getConstructionTypesDisplay,
      getFormerUseTypesDisplay,
      triggerFileInput,
      handleFileSelect,
      handleFileDrop,
      removeFile,
      showUploadModal,
      closeUploadModal,
      uploadDrawDocument,
      proceedToTemplateSetup,
      goToProject
    }
  }
}
</script>

<style scoped>
.project-confirmation-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: #f8f9fa;
  min-height: 100vh;
}

/* Header Section */
.confirmation-header {
  text-align: center;
  margin-bottom: 2rem;
}

.success-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.success-icon {
  font-size: 3rem;
}

.success-text h1 {
  color: #28a745;
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
}

.success-text p {
  color: #6c757d;
  margin: 0;
  font-size: 1.1rem;
}

/* Project Report Card */
.project-report-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.project-title-section {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.project-title-section h2 {
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
}

.project-id {
  color: #6c757d;
  font-size: 1.1rem;
}

/* Visual Section */
.visual-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.project-image-container,
.map-container {
  text-align: center;
}

.project-image-container h3,
.map-container h3 {
  color: #495057;
  margin-bottom: 1rem;
}

.image-wrapper,
.map-wrapper {
  border: 2px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  height: 300px;
}

.project-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: #f8f9fa;
  color: #6c757d;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.confirmation-map {
  width: 100%;
  height: 100%;
}

.map-error {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: #f8f9fa;
  color: #6c757d;
}

/* Project Details Grid */
.project-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.detail-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
}

.detail-section h3 {
  color: #495057;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #dee2e6;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  gap: 1rem;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item label {
  font-weight: 600;
  color: #495057;
  flex-shrink: 0;
  min-width: 40%;
}

.detail-item span {
  text-align: right;
  color: #212529;
  word-break: break-word;
}

.detail-item .empty-value {
  color: #9CA3AF;
  font-style: italic;
}

/* Upload Invitation Section */
.upload-invitation-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.upload-header {
  text-align: center;
  margin-bottom: 2rem;
}

.upload-header h2 {
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
}

.upload-header p {
  color: #6c757d;
  font-size: 1.1rem;
  margin: 0;
}

/* Form Styling */
.form-section {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
}

.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
}

.form-select:focus {
  outline: none;
  border-color: #4A5FD5;
  box-shadow: 0 0 0 3px rgba(74, 95, 213, 0.1);
}

/* Upload Section */
.upload-section {
  margin-top: 1.5rem;
}

.upload-instructions {
  text-align: center;
  margin-bottom: 1rem;
}

.upload-instructions p {
  color: #495057;
  margin: 0 0 0.25rem 0;
}

.upload-instructions small {
  color: #6c757d;
}

.file-upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  background: #f8f9fa;
  transition: all 0.3s ease;
  cursor: pointer;
}

.file-upload-area:hover,
.file-upload-area.drag-over {
  border-color: #4A5FD5;
  background: #f0f4ff;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-icon-large {
  font-size: 3rem;
  color: #6c757d;
}

.browse-button {
  background: #4A5FD5;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  text-decoration: underline;
}

.browse-button:hover {
  background: #3a4fa5;
}

.file-selected {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.file-icon {
  font-size: 1.5rem;
}

.file-name {
  font-weight: 500;
  color: #495057;
}

.remove-file {
  background: #dc3545;
  color: white;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-file:hover {
  background: #c82333;
}

/* Upload Actions */
.upload-actions {
  text-align: center;
  margin-top: 1.5rem;
}

.upload-button {
  background: #28a745;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 6px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.upload-button:hover:not(:disabled) {
  background: #218838;
}

.upload-button:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

/* Progress Section */
.progress-section {
  margin-top: 1rem;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #28a745;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  min-width: 50px;
}

.progress-status {
  text-align: center;
  color: #6c757d;
  margin: 0;
  font-size: 0.9rem;
}

/* Upload Actions Simple */
.upload-actions-simple {
  text-align: center;
  padding: 2rem;
}

.upload-button-main {
  background: #28a745;
  color: white;
  border: none;
  padding: 1.25rem 2.5rem;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 2rem;
  transition: background-color 0.3s ease;
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

.upload-button-main:hover {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(40, 167, 69, 0.4);
}

.upload-actions-simple p {
  color: #6c757d;
  margin: 0 0 1rem 0;
  font-size: 1rem;
}

.secondary-button {
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: background-color 0.3s ease;
}

.secondary-button:hover {
  background: #5a6268;
}

/* Modal Styles (from ProjectSummary) */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.upload-draw-modal {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: #dc3545;
}

.modal-body {
  padding: 1.5rem;
}

/* Progress Steps */
.ocr-progress-container {
  text-align: center;
  padding: 2rem;
}

.progress-steps {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin: 2rem 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.step.active {
  opacity: 1;
}

.step.completed {
  opacity: 1;
  color: #28a745;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.step.active .step-number {
  background: #4A5FD5;
  color: white;
}

.step.completed .step-number {
  background: #28a745;
  color: white;
}

.progress-message {
  color: #6c757d;
  font-style: italic;
}

/* Results */
.results-container {
  text-align: center;
  padding: 2rem;
}

.results-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.primary-button {
  background: #4A5FD5;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.primary-button:hover {
  background: #3a4fa5;
}

/* Responsive Design */
@media (max-width: 768px) {
  .project-confirmation-container {
    padding: 1rem;
  }
  
  .visual-section {
    grid-template-columns: 1fr;
  }
  
  .project-details-grid {
    grid-template-columns: 1fr;
  }
  
  .success-indicator {
    flex-direction: column;
    text-align: center;
  }
  
  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
  
  .detail-item label {
    min-width: auto;
  }
  
  .detail-item span {
    text-align: left;
  }
}
</style> 